import {
  KeyIcon,
  UserCircleIcon,
  CogIcon,
  ChatAltIcon,
  PencilAltIcon,
  TrashIcon,
  LockClosedIcon,
  DuplicateIcon,
  BellIcon,
  MailIcon,
  GiftIcon,
  CheckIcon,
  SelectorIcon,
} from "@heroicons/react/outline";
import dayjs from "dayjs";
import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat, PatternFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Switch,
  Transition,
} from "@headlessui/react";
import {
  useAuthDispatch,
  useAuthState,
  changePassword,
  requestUpdateUser,
  useConsultantDispatch,
  requestGetConsultant,
  useConsultantState,
  clearUpdateConsultant,
  clearConsultant,
  requestUpdateConsultant,
  requestListConsultantTemplates,
  clearCreateTemplate,
  requestCreateConsultantTemplate,
  requestUpdateConsultantTemplate,
  requestDeleteConsultantTemplate,
  resetListTemplateError,
  clearDeleteTemplate,
  requestListService,
  useConsultationDispatch,
  useConsultationState,
  requestGetUser,
  requestGetReferral,
  requestListReferralHistory,
  requestListPointTransactionReferral,
} from "../../Context";
import { CreditCardIcon } from "@heroicons/react/solid";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "../../Components/Modal";
import { ModalAlert } from "../../Components/ModalAlert";
import { ModalConfirm } from "../../Components/ModalConfirm";
import {
  cancelAPIRequest,
  isLastTokenNull,
  validateImage,
  getErrorMessages,
  generateYearList,
} from "../../Utils";
import WarningNote from "../../Components/WarningNote";
import {
  fetchMFAPreference,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from "aws-amplify/auth";
import QRCode from "react-qr-code";
import FormLabel from "../../Components/FormLabel";
import WhatsappIcon from "../../Assets/images/socials/whatsapp.svg";
import TableSkeleton from "../../Components/TableSkeleton";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { Pagination } from "../../Components/Pagination";
import FormSelect from "../../Components/v2/Form/FormSelect";

function ProfilePictureSkeleton() {
  return (
    <div className="animate-pulse col-span-12 flex gap-8 items-center">
      <div className="rounded-full bg-gray-300 w-24 h-24" />
      <div>
        <div className="flex">
          <div className="w-16 h-10 bg-gray-300 rounded-md"></div>
          <div className="w-16 h-10 bg-gray-300 rounded-md ml-3"></div>
        </div>
        <div className="w-36 h-4 bg-gray-300 rounded-md mt-2"></div>
      </div>
    </div>
  );
}

function InputSkeleton() {
  return (
    <div className="animate-pulse col-span-12">
      <div className="flex-1 space-y-2">
        <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
        <div className="block h-10 bg-gray-300 rounded-md"></div>
      </div>
    </div>
  );
}

function NoPicture() {
  return (
    <span className="rounded-full w-24 h-24 overflow-hidden bg-gray-100">
      <svg
        className="h-full w-full text-gray-300"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    </span>
  );
}

function EditProfile() {
  const { t } = useTranslation();

  const dispatch = useAuthDispatch();
  const { loading, error, user, updateUserStatus } = useAuthState();

  const [image, setimage] = useState(null);
  const [name, setname] = useState("");
  const [phone, setphone] = useState("");
  const [npwp, setnpwp] = useState("");
  const [company, setcompany] = useState("");
  const [npwpError, setnpwpError] = useState(null);
  const [submitted, setsubmitted] = useState(false);
  const [errorMessage, seterrorMessage] = useState("");

  useEffect(() => {
    if (user.image?.key) {
      setimage("");
    }
  }, [user.image]);

  useEffect(() => {
    setname(user.name || "");
  }, [user.name]);

  useEffect(() => {
    setphone(user.phone || "");
  }, [user.phone]);

  useEffect(() => {
    setnpwp(user.taxpayerInfo?.npwp || "");
  }, [user.taxpayerInfo]);

  useEffect(() => {
    if (submitted && npwp) {
      const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;
      if (isNpwpValid) {
        setErrorMessage("npwp", "");
      } else {
        setErrorMessage("npwp", "Invalid NPWP. Enter 15-16 digits.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npwp, submitted]);

  const handleNameChange = (event) => {
    setname(event.target.value);
  };

  const handleCompanyChange = (event) => {
    setcompany(event.target.value);
  };

  const handleProfilePictureChange = (event) => {
    const selectedFile = event.target.files[0];
    const allowedFormats = ["image/jpeg", "image/png", "image/gif"];
    const maxSizeMB = 1;

    const validation = validateImage(selectedFile, maxSizeMB, allowedFormats);
    if (!validation.isValid) {
      seterrorMessage(validation.errorMessage);
      setimage(null);
    } else {
      setimage(selectedFile);
      seterrorMessage("");
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "npwp":
        setnpwpError(message);
        break;
      default:
        break;
    }
  };

  const validateInput = () => {
    const isNpwpValid = npwp ? npwp.length >= 15 && npwp.length <= 16 : true;

    return isNpwpValid;
  };

  const handleSubmit = (event) => {
    setsubmitted(true);
    const isValid = validateInput();
    event.preventDefault();
    if (isValid) {
      const input = {
        name,
      };

      if (npwp) {
        if (user.taxpayerInfo) {
          input.taxpayerInfo = {
            ...user.taxpayerInfo,
            npwp,
          };
        } else {
          input.taxpayerInfo = {
            npwp,
          };
        }
      }

      if (phone) {
        input.phone = phone;
      }

      if (phone === "") {
        input.phone = null;
      }

      if (npwp === "") {
        input.taxpayerInfo = null;
      }

      if (image) {
        input.image = image;
      }

      if (image === null) {
        input.image = null;
      }

      requestUpdateUser(dispatch, { input });
    }
  };

  const renderProfilePicutre = () => {
    if (image === null) {
      return <NoPicture />;
    }

    if (image) {
      return (
        <img
          src={URL.createObjectURL(image)}
          alt={t("Profile")}
          className="rounded-full object-cover w-24 h-24"
        />
      );
    }

    if (user.imageUrl) {
      return (
        <img
          src={user.imageUrl}
          alt={t("Profile")}
          className="rounded-full object-cover w-24 h-24"
        />
      );
    }

    return <NoPicture />;
  };

  return (
    <form
      className="divide-y divide-gray-200 lg:col-span-9"
      action="#"
      onSubmit={handleSubmit}
    >
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("Profile")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("Only name will be displayed publicly.")}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-12 gap-6 overflow-auto">
          {updateUserStatus === "SUCCESS" ? (
            <div className="col-span-12">
              <Alert
                type="success"
                message={t("Profile updated successfully")}
              />
            </div>
          ) : null}

          {error ? (
            <div className="col-span-12">
              <Alert
                type="error"
                message={error?.message || "Error update profile."}
                errors={error?.errors}
              />
            </div>
          ) : null}

          {user.membership === "CONSULTANT" && (
            <>
              {loading ? (
                <ProfilePictureSkeleton />
              ) : (
                <div className="col-span-12 flex gap-8 items-center">
                  {renderProfilePicutre()}
                  <div>
                    <div className="flex">
                      <div className="relative bg-white py-2 px-3 border border-blue-gray-300 rounded-md shadow-sm flex items-center cursor-pointer hover:bg-blue-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-blue-gray-50 focus-within:ring-blue-500">
                        <label
                          htmlFor="user-photo"
                          className="relative text-sm font-medium text-blue-gray-900 pointer-events-none"
                        >
                          <span>{t("Change")}</span>
                          <span className="sr-only"> {t("profile photo")}</span>
                        </label>
                        <input
                          id="user-photo"
                          name="user-photo"
                          type="file"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md"
                          onChange={handleProfilePictureChange}
                        />
                      </div>
                      <button
                        onClick={() => setimage(null)}
                        type="button"
                        className="ml-3 bg-transparent py-2 px-3 border border-transparent rounded-md text-sm font-medium text-blue-gray-900 hover:text-blue-gray-700 focus:outline-none focus:border-blue-gray-300 focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-gray-50 focus:ring-blue-500"
                      >
                        {t("Remove")}
                      </button>
                    </div>
                    <p className="text-gray-600 text-xs mt-2">
                      {image?.name
                        ? image.name
                        : t("JPG, GIF or PNG. 1MB max.")}
                    </p>
                    {errorMessage && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(errorMessage)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Name")}
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={name}
                onChange={handleNameChange}
                placeholder={t("Enter your name")}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
          )}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Phone")}
              </label>
              <FormPhoneInput
                name="phone"
                id="phone"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
                placeholder={t("Enter your phone number")}
                value={phone}
                onChange={setphone}
              />
            </div>
          )}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="npwp"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Tax Identification Number (NPWP)")}
              </label>
              <NumericFormat
                type="text"
                name="npwp"
                id="npwp"
                allowLeadingZeros
                className={classNames(
                  npwpError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500 focus:outline-none focus:ring-0"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500 focus:outline-none focus:ring-0",
                  "mt-1 block w-full text-sm rounded-md shadow-sm",
                )}
                placeholder={t("Enter the tax identification number (NPWP)")}
                displayType="input"
                value={npwp || ""}
                onValueChange={(value) => {
                  setnpwp(value.value);
                }}
                isAllowed={({ value }) => value.length <= 16}
              />
              {npwpError && (
                <div className="mt-1 text-red-600 text-sm">{t(npwpError)}</div>
              )}
            </div>
          )}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="organization"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Company Name")}
              </label>
              <input
                type="text"
                name="organization"
                id="organization"
                value={company}
                onChange={handleCompanyChange}
                placeholder={t("Enter your company name")}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
              />
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 py-4 px-4 flex justify-end sm:px-6">
        <Button
          type="submit"
          title={t("Save")}
          disabled={loading}
          loading={loading}
        />
      </div>
    </form>
  );
}

function NpwpService() {
  const { t } = useTranslation();

  const { loading, user } = useAuthState();

  return (
    <div className="col-span-9">
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("NPWP")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("NPWP that has been registered through Pajakind.")}
          </p>
        </div>
        <div className="max-w-md mt-6">
          <div className="bg-white shadow rounded-lg">
            <div className="grid gap-6">
              <div className="bg-white shadow rounded-lg h-52 relative text-white">
                <img
                  alt="background"
                  className="relative object-cover w-full h-full rounded-lg"
                  src="https://pajakind-images.s3.ap-southeast-1.amazonaws.com/npwp-bg.png"
                />
                <div className="w-full px-4 absolute top-4">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-light text-xs">{t("Name")}</p>
                      {loading ? (
                        <div className="animate-pulse h-4 mt-[4px] mb-[4px] w-32 bg-gray-300 rounded-md"></div>
                      ) : (
                        <p className="font-medium">
                          {user?.registeredNpwp?.name}
                        </p>
                      )}
                    </div>
                    <img
                      alt="npwp-logo"
                      className="h-8"
                      src="https://pajakind-images.s3.ap-southeast-1.amazonaws.com/npwp-logo.png"
                    />
                  </div>
                  <div className="pt-6">
                    <p className="font-light text-xs">{t("NPWP")}</p>
                    {loading ? (
                      <div className="animate-pulse h-4 mt-[4px] mb-[4px] w-32 bg-gray-300 rounded-md"></div>
                    ) : (
                      <NumericFormat
                        className="font-medium"
                        displayType={"text"}
                        value={user?.registeredNpwp?.npwp}
                      />
                    )}
                  </div>
                  <div className="pt-6">
                    <div className="flex justify-between">
                      <div className="">
                        <p className="font-light text-xs">
                          {t("Transaction Number")}
                        </p>
                        {loading ? (
                          <div className="animate-pulse h-3 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
                        ) : (
                          <PatternFormat
                            className="font-medium"
                            displayType={"text"}
                            format="###############"
                            value={user?.registeredNpwp?.transactionID}
                          />
                        )}
                      </div>
                      <div className="">
                        <p className="font-light text-xs">{t("KPP")}</p>
                        {loading ? (
                          <div className="animate-pulse h-3 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
                        ) : (
                          <p className="font-medium text-sm">
                            {user?.registeredNpwp?.kppName}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditPassword() {
  const { t } = useTranslation();

  const dispatch = useAuthDispatch();
  const {
    changePasswordLoading,
    changePasswordError,
    changePasswordStatus,
    loading,
  } = useAuthState();

  const handleSubmit = (event) => {
    event.preventDefault();
    const currentPassword = event.target.elements.current_password?.value || "";
    const newPassword = event.target.elements.new_password?.value || "";
    changePassword(dispatch, { currentPassword, newPassword });
  };

  return (
    <form
      className="divide-y divide-gray-200 lg:col-span-9"
      action="#"
      onSubmit={handleSubmit}
    >
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("Password")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("Change your current password.")}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-12 gap-6">
          {changePasswordStatus === "SUCCESS" ? (
            <div className="col-span-12">
              <Alert
                type="success"
                message={t("Successfully change password")}
              />
            </div>
          ) : null}

          {changePasswordError ? (
            <div className="col-span-12">
              <Alert
                type="error"
                message={
                  changePasswordError?.message || "Error change password."
                }
                errors={changePasswordError?.errors}
              />
            </div>
          ) : null}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="current_password"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Current Password")}
              </label>
              <input
                type="password"
                name="current_password"
                id="current_password"
                placeholder={t("Enter your current password")}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
          )}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="new_password"
                className="block text-sm font-medium text-gray-700"
              >
                {t("New Password")}
              </label>
              <input
                type="password"
                name="new_password"
                id="new_password"
                placeholder={t("Enter your new password")}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
          )}

          {loading ? (
            <InputSkeleton />
          ) : (
            <div className="col-span-12">
              <label
                htmlFor="retype_new_password"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Retype New Password")}
              </label>
              <input
                type="password"
                name="retype_new_password"
                id="retype_new_password"
                placeholder={t("Retype your new password")}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-0 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 py-4 px-4 flex justify-end sm:px-6">
        <Button
          type="submit"
          title={t("Save")}
          disabled={changePasswordLoading}
          loading={changePasswordLoading}
        />
      </div>
    </form>
  );
}

function ConsultantSettings() {
  const { t } = useTranslation();
  const alertRef = useRef(null);

  const [active, setactive] = useState(false);
  const [acceptCall, setacceptCall] = useState(false);
  const [acceptZoom, setacceptZoom] = useState(false);
  const [selectedServices, setselectedServices] = useState({});
  const [selectedDays, setselectedDays] = useState({});
  const [startTime, setStartTime] = useState(9);
  const [endTime, setEndTime] = useState(17);
  const [workStartYear, setWorkStartYear] = useState(null);

  // Validation error states
  const [validationErrors, setValidationErrors] = useState({
    services: "",
    days: "",
    time: "",
  });

  const { user } = useAuthState();

  const days = [
    { id: 0, name: "Sunday" },
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
  ];

  const timeOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, "0")}:00`,
  }));

  const dispatch = useConsultantDispatch();
  const consultationDispatch = useConsultationDispatch();

  const {
    updateConsultantStatus,
    consultant,
    submitLoading,
    submitError,
    loading,
  } = useConsultantState();
  const { serviceLoading, services, serviceError } = useConsultationState();

  useEffect(() => {
    requestListService(consultationDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const yearList = useMemo(() => generateYearList(), []);

  useEffect(() => {
    requestGetConsultant(dispatch, { userID: user.id });
    return () => {
      clearConsultant(dispatch);
      clearUpdateConsultant(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (consultant?.active) {
      setactive(consultant.active);
    }
    if (consultant?.acceptCall) {
      setacceptCall(consultant.acceptCall);
    }
    if (consultant?.acceptZoom) {
      setacceptZoom(consultant.acceptZoom);
    }
    if (consultant?.services?.items?.length > 0) {
      const allServices = {};
      consultant.services.items.forEach((item) => {
        allServices[item.serviceID] = true;
      });
      setselectedServices(allServices);
    }
    if (consultant?.activeDays?.length > 0) {
      const allDays = {};
      consultant.activeDays.forEach((isActive, dayIndex) => {
        if (isActive) {
          allDays[dayIndex] = true;
        }
      });
      setselectedDays(allDays);
    }
    if (consultant?.startTimes?.length > 0) {
      setStartTime(consultant.startTimes[0]);
    }
    if (consultant?.endTimes?.length > 0) {
      setEndTime(consultant.endTimes[0]);
    }
    if (consultant?.workStartYear) {
      setWorkStartYear(
        yearList.find((year) => year.value === consultant.workStartYear),
      );
    }
  }, [consultant, yearList]);

  // Auto-scroll to alert when errors occur
  useEffect(() => {
    if ((submitError || serviceError) && alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [submitError, serviceError]);

  // Validation function
  const validateForm = () => {
    const errors = {
      services: "",
      days: "",
      time: "",
    };

    // Validate services - at least 1 service must be selected
    const selectedServiceCount =
      Object.values(selectedServices).filter(Boolean).length;
    if (selectedServiceCount === 0) {
      errors.services = "At least one service must be selected";
    }

    // Validate days - minimum 5 days must be selected
    const selectedDayCount = Object.values(selectedDays).filter(Boolean).length;
    if (selectedDayCount < 5) {
      errors.days = "Minimum 5 days must be selected";
    }

    // Validate time - end time must be greater than start time and 6 hours minimum difference
    if (endTime <= startTime) {
      errors.time = "End time must be greater than start time";
    } else if (endTime - startTime < 6) {
      errors.time =
        "There must be at least 6 hours between start time and end time";
    }

    setValidationErrors(errors);

    // Return true if no errors
    return !errors.services && !errors.days && !errors.time;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Run validation before proceeding
    if (!validateForm()) {
      return;
    }

    const newSelectedServices = { ...selectedServices };
    const addServices = [];
    const deleteServices = [];

    Object.keys(newSelectedServices).forEach((serviceID) => {
      if (newSelectedServices[serviceID]) {
        addServices.push(serviceID);
      } else {
        if (consultant.services?.items?.length > 0) {
          for (
            let index = 0;
            index < consultant.services.items.length;
            index += 1
          ) {
            const service = consultant.services.items[index];
            if (service.serviceID === serviceID) {
              deleteServices.push(service.id);
              break;
            }
          }
        }
      }
    });

    const activeDays = new Array(7).fill(false);
    Object.keys(selectedDays).forEach((dayId) => {
      if (selectedDays[dayId]) {
        activeDays[parseInt(dayId)] = true;
      }
    });

    const startTimes = new Array(7).fill(startTime);
    const endTimes = new Array(7).fill(endTime);

    requestUpdateConsultant(dispatch, {
      active,
      acceptCall,
      acceptZoom,
      addServices,
      deleteServices,
      activeDays,
      startTimes,
      endTimes,
      workStartYear: workStartYear?.value,
    });
  };

  const CheckboxSkeleton = () => {
    return (
      <div className="mt-4 border border-b border-gray-200 divide-y divide-gray-200 rounded col-span-12">
        <div className="relative flex items-start p-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="ml-3 flex items-center h-5">
            <div className="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
          </div>
        </div>
        <div className="relative flex items-start p-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="ml-3 flex items-center h-5">
            <div className="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
          </div>
        </div>
        <div className="relative flex items-start p-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="ml-3 flex items-center h-5">
            <div className="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
          </div>
        </div>
        <div className="relative flex flex-col p-4 gap-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <div className="animate-pulse bg-gray-300 h-4 w-20 rounded-md"></div>
              <div className="animate-pulse bg-gray-300 h-10 w-full rounded-lg"></div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="animate-pulse bg-gray-300 h-4 w-20 rounded-md"></div>
              <div className="animate-pulse bg-gray-300 h-10 w-full rounded-lg"></div>
            </div>
          </div>
        </div>
        <div className="relative flex flex-col p-4 gap-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="min-w-0 flex-1 text-sm flex flex-col gap-4">
            <div className="flex justify-between">
              <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
              <div className="ml-3 flex items-center h-5">
                <div className="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
              <div className="ml-3 flex items-center h-5">
                <div className="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
              <div className="ml-3 flex items-center h-5">
                <div className="animate-pulse bg-gray-300 h-4 w-4 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <form
      className="divide-y divide-gray-200 lg:col-span-9"
      action="#"
      onSubmit={handleSubmit}
    >
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("Consultant Settings")}
          </h2>
        </div>
        <div className="mt-6 grid grid-cols-12 gap-6 overflow-auto">
          {updateConsultantStatus === "SUCCESS" ? (
            <div className="col-span-12">
              <Alert
                type="success"
                message={t("Consultant settings updated successfully")}
              />
            </div>
          ) : null}
          {submitError || serviceError ? (
            <div className="col-span-12" ref={alertRef}>
              <Alert
                type="error"
                message={
                  submitError
                    ? getErrorMessages(submitError)[0] ||
                      "Error updating consultant settings."
                    : getErrorMessages(serviceError)[0] ||
                      "Services is not found."
                }
                errors={
                  submitError
                    ? getErrorMessages(submitError)
                    : getErrorMessages(serviceError)
                }
              />
            </div>
          ) : null}
          {submitLoading || loading || serviceLoading ? (
            <CheckboxSkeleton />
          ) : (
            <div className="border border-b border-gray-200 divide-y divide-gray-200 rounded col-span-12">
              <div className="relative flex items-start p-4">
                <div className="min-w-0 flex-1 text-sm">
                  <label
                    htmlFor="consultation"
                    className="font-medium text-gray-700 select-none"
                  >
                    {t("Accept Consultation")}
                  </label>
                </div>
                <div className="ml-3 flex items-center h-5">
                  <input
                    id="consultation"
                    name="consultation"
                    type="checkbox"
                    checked={active}
                    onChange={() => {
                      setactive(!active);
                    }}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                  />
                </div>
              </div>
              <div className="relative flex items-start p-4">
                <div className="min-w-0 flex-1 text-sm">
                  <label
                    htmlFor="video-call"
                    className="font-medium text-gray-700 select-none"
                  >
                    {t("Receive Video Audio Call")}
                  </label>
                </div>
                <div className="ml-3 flex items-center h-5">
                  <input
                    id="video-call"
                    name="video-call"
                    type="checkbox"
                    checked={acceptCall}
                    onChange={() => {
                      setacceptCall(!acceptCall);
                    }}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                  />
                </div>
              </div>
              <div className="relative flex items-start p-4">
                <div className="min-w-0 flex-1 text-sm">
                  <label
                    htmlFor="zoom-call"
                    className="font-medium text-gray-700 select-none"
                  >
                    {t("Receive Zoom Call")}
                  </label>
                </div>
                <div className="ml-3 flex items-center h-5">
                  <input
                    id="zoom-call"
                    name="zoom-call"
                    type="checkbox"
                    checked={acceptZoom}
                    onChange={() => {
                      setacceptZoom(!acceptZoom);
                    }}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                  />
                </div>
              </div>
              <div className="p-4">
                <WarningNote message={t("zoomCallNotice")} />
              </div>
              <div className="relative flex flex-col p-4 gap-4">
                <div className="min-w-0 flex-1 text-sm">
                  <label className="font-medium text-gray-700 select-none">
                    {t("Services")}
                  </label>
                </div>
                <div className="min-w-0 flex-1 text-sm flex flex-col gap-4">
                  {services
                    .filter((service) => service.id !== "business-license")
                    .map((service) => (
                      <div key={service.id} className="flex justify-between">
                        <label
                          htmlFor={service.id}
                          className="text-gray-700 select-none"
                        >
                          {t(service.name)}
                        </label>
                        <div className="ml-3 flex items-center h-5">
                          <input
                            id={service.id}
                            name={service.id}
                            type="checkbox"
                            checked={!!selectedServices[service.id]}
                            onChange={() =>
                              setselectedServices({
                                ...selectedServices,
                                [service.id]: !selectedServices[service.id],
                              })
                            }
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                          />
                        </div>
                      </div>
                    ))}
                </div>
                {validationErrors.services && (
                  <div className="text-red-600 text-sm">
                    {t(validationErrors.services)}
                  </div>
                )}
              </div>
              <div className="relative flex flex-col p-4 gap-4">
                <div className="min-w-0 flex-1 text-sm">
                  <label className="font-medium text-gray-700 select-none">
                    {t("Working Hours")}
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-700 select-none">
                      {t("Start Time")}
                    </label>
                    <select
                      value={startTime}
                      onChange={(e) => setStartTime(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-700 select-none">
                      {t("End Time")}
                    </label>
                    <select
                      value={endTime}
                      onChange={(e) => setEndTime(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {validationErrors.time && (
                  <div className="text-red-600 text-sm">
                    {t(validationErrors.time)}
                  </div>
                )}
              </div>
              <div className="relative flex flex-col p-4 gap-4">
                <FormSelect
                  label="experienceSince"
                  id="workStartYear"
                  name="workStartYear"
                  value={workStartYear}
                  options={yearList}
                  onChange={setWorkStartYear}
                  placeholder={t("selectYearStartedWorking")}
                  searchable
                />
              </div>
              <div className="relative flex flex-col p-4 gap-4">
                <div className="min-w-0 flex-1 text-sm">
                  <label className="font-medium text-gray-700 select-none">
                    {t("availableDays")}
                  </label>
                </div>
                <div className="min-w-0 flex-1 text-sm flex flex-col gap-4">
                  {days.map((day) => (
                    <div key={day.id} className="flex justify-between">
                      <label
                        htmlFor={`day-${day.id}`}
                        className="text-gray-700 select-none"
                      >
                        {t(day.name)}
                      </label>
                      <div className="ml-3 flex items-center h-5">
                        <input
                          id={`day-${day.id}`}
                          name={`day-${day.id}`}
                          type="checkbox"
                          checked={!!selectedDays[day.id]}
                          onChange={() =>
                            setselectedDays({
                              ...selectedDays,
                              [day.id]: !selectedDays[day.id],
                            })
                          }
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {validationErrors.days && (
                  <div className="text-red-600 text-sm">
                    {t(validationErrors.days)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 py-4 px-4 flex justify-end sm:px-6">
        <Button
          type="submit"
          title={t("Save")}
          disabled={submitLoading}
          loading={submitLoading}
        />
      </div>
    </form>
  );
}

function ConsultantTemplate() {
  const { t } = useTranslation();

  const [templateFormModal, setTemplateFormModal] = useState(false);
  const [templateText, setTemplateText] = useState("");
  const [updateTemplateID, setupdateTemplateID] = useState("");
  const [templateID, setTemplateID] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [page, setPage] = useState(1);

  const dispatch = useConsultantDispatch();

  const {
    createTemplateError,
    createTemplateLoading,
    createTemplateStatus,
    templates,
    templatesLoading,
    templatesError,
    deleteTemplateLoading,
    deleteTemplateError,
    templateStatus,
    nextTokens,
    nextLoading,
    deletedID,
  } = useConsultantState();

  useEffect(() => {
    requestListConsultantTemplates(dispatch, {
      page: 1,
      limit: 10,
    });
    return () => {
      clearCreateTemplate(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (createTemplateStatus === "SUCCESS") {
      setTemplateText("");
      setupdateTemplateID("");
      setShowSuccessModal(true);
      setTemplateFormModal(false);
    }
    if (deletedID) {
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    }
  }, [createTemplateStatus, deletedID]);

  useEffect(() => {
    resetState();
    setShowErrorModal(true);
    setTemplateFormModal(false);
  }, [createTemplateError]);

  const resetState = () => {
    setTemplateText("");
    setupdateTemplateID("");
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (updateTemplateID === "") {
      requestCreateConsultantTemplate(dispatch, { text: templateText });
    } else {
      requestUpdateConsultantTemplate(dispatch, {
        id: updateTemplateID,
        text: templateText,
      });
    }
  };

  const onDelete = () => {
    requestDeleteConsultantTemplate(dispatch, {
      id: templateID,
    });
  };

  const onLoadMore = (e) => {
    e.preventDefault();
    const nextPage = templatesError ? page : page + 1;

    if (templatesError || nextTokens[nextPage]) {
      resetListTemplateError(dispatch);
      setPage(nextPage);
      requestListConsultantTemplates(dispatch, {
        page: nextPage,
        limit: 10,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const TemplateSkeleton = () => {
    return (
      <div className="col-span-12 space-y-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="animate-pulse bg-white rounded-xl border border-gray-100 p-5"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-2.5">
                <div className="h-3.5 bg-gray-200 rounded-full w-4/5"></div>
                <div className="h-3 bg-gray-100 rounded-full w-3/5"></div>
              </div>
              <div className="ml-4 flex items-center gap-2">
                <div className="h-8 w-8 bg-gray-100 rounded-lg"></div>
                <div className="h-8 w-8 bg-gray-100 rounded-lg"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  function RenderTemplate(template) {
    return (
      <div
        key={template.id}
        className="group bg-white rounded-lg border border-gray-200 p-4 transition-all duration-200 hover:border-gray-300"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">
              {template.text}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              title={t("Edit")}
              onClick={(e) => {
                e.preventDefault();
                setupdateTemplateID(template.id);
                setTemplateText(template.text);
                setTemplateFormModal(true);
              }}
              className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-150"
            >
              <PencilAltIcon className="h-4 w-4" />
            </button>
            <button
              title={t("Delete")}
              onClick={(e) => {
                e.preventDefault();
                setShowConfirmModal(true);
                setTemplateID(template.id);
              }}
              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-150"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ModalTemplateFooter = () => {
    return (
      <div className="px-6 py-4 flex justify-end gap-3 bg-gray-50/80 border-t border-gray-100 rounded-b-lg">
        <Button
          outline
          disabled={createTemplateLoading}
          title="Cancel"
          onClick={() => {
            setTemplateFormModal(false);
            resetState();
          }}
        />
        <Button
          disabled={createTemplateLoading || !templateText.trim()}
          loading={createTemplateLoading}
          title="Save"
          onClick={handleSave}
        />
      </div>
    );
  };

  return (
    <form className="divide-y divide-gray-200 lg:col-span-9" action="#">
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        {/* Header */}
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("Consultant Template")}
          </h2>
        </div>

        {/* Template List */}
        <div className="mt-6 grid grid-cols-12 overflow-auto">
          {/* Modals */}
          <ModalAlert
            open={showConfirmModal}
            setOpen={(open) => {
              setShowConfirmModal(open);
            }}
            title="Delete Template"
            description="Are you sure you want to delete this template?"
            confirmButtonText="Yes"
            cancelButtonText="Cancel"
            loadingButton={deleteTemplateLoading}
            disabledButton={deleteTemplateLoading}
            onConfirm={onDelete}
          />
          <ModalConfirm
            open={showSuccessModal}
            type="SUCCESS"
            setOpen={(open) => {
              setShowSuccessModal(open);
            }}
            title="Success"
            description={`Template ${templateStatus.toLowerCase()} successfully`}
            onConfirm={() => {
              clearCreateTemplate(dispatch);
              clearDeleteTemplate(dispatch);
            }}
          />
          <Modal
            show={templateFormModal}
            onClose={() => {
              setTemplateFormModal(false);
              resetState();
            }}
            title={updateTemplateID ? t("Edit Template") : t("Create Template")}
            footer={<ModalTemplateFooter />}
          >
            <div className="flex flex-col px-6 py-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("Template Message")}
              </label>
              <textarea
                className="w-full rounded-lg border-gray-200 shadow-sm text-sm text-gray-700 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 resize-none"
                rows={5}
                placeholder={t("Type your template message here...")}
                onChange={(e) => setTemplateText(e.target.value)}
                value={templateText}
                maxLength={1000}
              />
              <div className="flex justify-end mt-2">
                <span
                  className={`text-xs ${templateText.length >= 900 ? "text-amber-500" : "text-gray-400"}`}
                >
                  {templateText.length}/1000
                </span>
              </div>
            </div>
          </Modal>
          {createTemplateError || deleteTemplateError ? (
            <ModalConfirm
              open={showErrorModal}
              type="ALERT"
              setOpen={(open) => {
                setShowErrorModal(open);
              }}
              title="Error"
              description={
                createTemplateError?.errors[0]?.message ||
                deleteTemplateError?.errors[0]?.message
              }
              onConfirm={() => {
                clearDeleteTemplate(dispatch);
                setShowConfirmModal(false);
              }}
            />
          ) : null}

          {/* Error Alert */}
          {templatesError ? (
            <div className="col-span-12">
              <Alert
                type="error"
                message={
                  templatesError?.message ||
                  "Error updating consultant settings."
                }
                errors={templatesError?.errors}
              />
            </div>
          ) : null}

          {/* Template Cards */}
          {templatesLoading || nextLoading ? (
            <TemplateSkeleton />
          ) : (
            <div className="col-span-12 space-y-3">
              {templates.length > 0 ? (
                templates.map((template) => {
                  return RenderTemplate(template);
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
                    <ChatAltIcon className="h-8 w-8 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    {t("No Template")}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t("Create your first template to get started")}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Load More */}
          {!templatesLoading && isLastTokenNull(nextTokens) ? null : (
            <div className="flex justify-center items-center pt-5 pb-1 col-span-12">
              <button
                onClick={onLoadMore}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors duration-150"
              >
                {t("Load More")}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 py-4 px-4 flex justify-end sm:px-6">
        <Button
          type="button"
          title={t("Add Template")}
          onClick={(e) => setTemplateFormModal(true)}
        />
      </div>
    </form>
  );
}

function PreferredMFA() {
  const { t } = useTranslation();

  const { user } = useAuthState();

  const [showOtpVerifyModal, setshowOtpVerifyModal] = useState(false);
  const [showMfaEnabelModal, setshowMfaEnabelModal] = useState(false);
  const [showDialogEnabelMfaModal, setshowDialogEnabelMfaModal] =
    useState(false);

  const [userMFA, setUserMFA] = useState({});
  const [preferredMFA, setPreferredMFA] = useState(null);
  const [qrCode, setqrCode] = useState("");
  const [code, setcode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setloading] = useState(false);
  const [errorMessage, seterrorMessage] = useState("");
  const [successMessage, setsuccessMessage] = useState("");

  const fetchMFASettings = async () => {
    try {
      setloading(true);
      const data = await fetchMFAPreference();
      setUserMFA(data);
      if (data.enabled?.includes("TOTP")) {
        setPreferredMFA("TOTP");
      } else {
        setPreferredMFA("NOMFA");
      }
    } catch (error) {
      let message = "Error fetching MFA settings";
      if (error.message) {
        message = error.message;
      }
      seterrorMessage(message);
      setloading(false);
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    fetchMFASettings();
  }, []);

  useEffect(() => {
    if (showMfaEnabelModal) {
      const startSetup = async () => {
        try {
          setloading(true);
          const output = await setUpTOTP();
          const setupUri = output.getSetupUri("PajakInd", user?.email);
          const totpSecret = output.sharedSecret;
          setqrCode(setupUri.href);
          setcode(totpSecret);
        } catch (error) {
          let message = "Error setting up TOTP";
          if (error.message) {
            message = error.message;
          }
          seterrorMessage(message);
        } finally {
          setloading(false);
        }
      };

      startSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMfaEnabelModal]);

  useEffect(() => {
    if (["NOMFA", "TOTP"].includes(preferredMFA) && successMessage !== "") {
      setshowOtpVerifyModal(false);
    }
  }, [preferredMFA, successMessage]);

  const handleVerify = async () => {
    try {
      setloading(true);
      await verifyTOTPSetup({ code: verificationCode });
      await updateMFAPreference({ totp: "PREFERRED" });
      setPreferredMFA("TOTP");
      setsuccessMessage("Successfully enabled two-factor authentication");
      setqrCode("");
      setVerificationCode("");
      fetchMFASettings();
      seterrorMessage("");
    } catch (error) {
      let message = "The Authenticator code is not valid";
      if (error.message) {
        message = error.message;
      }
      seterrorMessage(message);
    } finally {
      setloading(false);
    }
  };

  const handleDisabledVerify = async () => {
    try {
      setloading(true);
      await verifyTOTPSetup({ code: verificationCode });
      await updateMFAPreference({ totp: "DISABLED" });
      setsuccessMessage("Authenticator successfully removed");
      setPreferredMFA("NOMFA");
      setVerificationCode("");
      fetchMFASettings();
      seterrorMessage("");
    } catch (error) {
      let message = "The Authenticator code is not valid";
      if (error.message) {
        message = error.message;
      }
      seterrorMessage(message);
    } finally {
      setloading(false);
    }
  };

  const renderOtpVerifyModal = () => {
    const handleVerificationCodeChange = (e) => {
      const input = e.target.value;
      const sanitizedInput = input.replace(/\D/g, "");
      const truncatedInput = sanitizedInput.slice(0, 6);

      setVerificationCode(truncatedInput);
    };

    const handlePaste = () => {
      navigator.clipboard.readText().then((text) => {
        handleVerificationCodeChange({
          target: { name: "verificationCode", value: text },
        });
      });
    };

    return (
      <Modal
        title={
          !userMFA?.enabled ? "Connect Authenticator" : "Security Verification"
        }
        show={showOtpVerifyModal}
        onClose={() => {
          setshowOtpVerifyModal(false);
          seterrorMessage("");
          setVerificationCode("");
        }}
        footer={
          <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
            <Button
              disabled={loading || verificationCode.length < 6}
              loading={loading}
              title="Send"
              onClick={() => {
                if (!userMFA?.enabled) {
                  handleVerify();
                } else {
                  handleDisabledVerify();
                }
              }}
            />
          </div>
        }
      >
        <div className="text-sm p-4">
          <FormLabel
            htmlFor="verificationCode"
            label={t("Authenticator code")}
            required
          />
          <div className="relative mt-1">
            <input
              maxLength="6"
              inputMode="numeric"
              spellCheck="false"
              autoComplete="one-time-code"
              type="text"
              name="verificationCode"
              value={verificationCode}
              placeholder={t("Enter the 6-digit code from the authenticator")}
              onChange={handleVerificationCodeChange}
              className={
                "border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm pr-[70px]"
              }
            />
            <button
              onClick={handlePaste}
              className="text-sm text-blue-600 font-medium absolute inset-y-0 inline-flex items-center right-4"
            >
              {t("Paste")}
            </button>
          </div>
          {errorMessage && (
            <div className="mt-1 text-red-600 text-sm">{t(errorMessage)}</div>
          )}
        </div>
      </Modal>
    );
  };

  const renderMfaEnabelModal = () => {
    return (
      <Modal
        title="Connect Authenticator"
        show={showMfaEnabelModal}
        onClose={() => {
          setshowMfaEnabelModal(false);
        }}
        footer={
          <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
            <Button
              title={t("Continue")}
              onClick={() => {
                setshowMfaEnabelModal(false);
                setshowOtpVerifyModal(true);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col text-sm text-gray-900 gap-6 p-4">
          <span>{t("Scan the QR code below using the Authenticator app")}</span>
          <div className="flex items-center justify-center">
            <QRCode size={110} value={qrCode} />
          </div>
          <div className="flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-900">{code}</span>
            <button onClick={() => navigator.clipboard.writeText(code)}>
              <DuplicateIcon className="flex-none text-gray-500 h-6 w-6" />
            </button>
          </div>
          <div className="flex items-center justify-center text-center gap-4">
            {t(
              "If you are unable to scan the code, you can enter this secret key into your authenticator app",
            )}
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className="divide-y divide-gray-200 lg:col-span-9">
      <div className="flex flex-col py-6 px-4 sm:p-6 lg:pb-8 gap-6">
        <ModalAlert
          open={showDialogEnabelMfaModal}
          setOpen={(open) => {
            setshowDialogEnabelMfaModal(open);
          }}
          title="Delete Authenticator"
          description="Are you sure you want to remove the authenticator? It's recommended to keep using the authenticator to enhance the security of your account."
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          onConfirm={() => {
            setshowOtpVerifyModal(true);
            setshowDialogEnabelMfaModal(false);
          }}
        />

        {renderOtpVerifyModal()}
        {renderMfaEnabelModal()}
        {userMFA?.preferred === "TOTP" &&
        preferredMFA === "TOTP" &&
        successMessage !== "" ? (
          <div className="col-span-12">
            <Alert type="success" message={t(successMessage)} />
          </div>
        ) : null}

        {!userMFA?.enabled &&
        preferredMFA === "NOMFA" &&
        successMessage !== "" ? (
          <div className="col-span-12">
            <Alert type="success" message={t(successMessage)} />
          </div>
        ) : null}

        {errorMessage !== "" && !showOtpVerifyModal ? (
          <div className="col-span-12">
            <Alert type="error" message={errorMessage} />
          </div>
        ) : null}

        <div className="flex flex-col">
          <span className="text-lg leading-6 font-medium text-gray-900">
            {t("Multi-Factor Authentication")}
          </span>
          <span className="mt-1 text-sm text-gray-500">
            {t(
              "Help protect your account from unauthorized access by requesting a second authentication code in addition to your password.",
            )}
          </span>
        </div>
        {!userMFA?.enabled ? (
          <div>
            <Button
              title={t("Activate Authenticator")}
              onClick={() => setshowMfaEnabelModal(true)}
            />
          </div>
        ) : (
          <div>
            <Button
              color={"red"}
              title={t("Delete Authenticator")}
              onClick={() => setshowDialogEnabelMfaModal(true)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationSettings() {
  const { t } = useTranslation();

  const [acceptEmail, setacceptEmail] = useState(false);
  const [acceptWhatsApp, setacceptWhatsApp] = useState(false);

  const dispatch = useAuthDispatch();
  const { loading, error, user, updateUserStatus } = useAuthState();

  useEffect(() => {
    if (user.sub) {
      requestGetUser(dispatch, { sub: user.sub });
    }
  }, [dispatch, user.sub]);

  useEffect(() => {
    if (user?.offersNotification) {
      const { email, whatsapp } = user.offersNotification;
      setacceptEmail(email);
      setacceptWhatsApp(whatsapp);
    }
  }, [user?.offersNotification]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const input = {
      offersNotification: {
        email: acceptEmail,
        whatsapp: acceptWhatsApp,
      },
    };

    requestUpdateUser(dispatch, { input });
  };

  const SwitchSkeleton = () => {
    return (
      <div className="mt-4 border border-b border-gray-200 divide-y divide-gray-200 rounded col-span-12">
        <div className="relative flex items-start p-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="ml-3 flex items-center h-5">
            <div className="animate-pulse bg-gray-300 h-6 w-12 rounded-full"></div>
          </div>
        </div>
        <div className="relative flex items-start p-4">
          <div className="min-w-0 flex-1 text-sm">
            <div className="animate-pulse bg-gray-300 h-5 w-40 mb-1 rounded-md"></div>
          </div>
          <div className="ml-3 flex items-center h-5">
            <div className="animate-pulse bg-gray-300 h-6 w-12 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <form
      className="divide-y divide-gray-200 lg:col-span-9"
      action="#"
      onSubmit={handleSubmit}
    >
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("offers")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("productServiceOffers")}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-12 gap-6 overflow-auto">
          {updateUserStatus === "SUCCESS" ? (
            <div className="col-span-12">
              <Alert
                type="success"
                message={t("notificationsSettingsUpdated")}
              />
            </div>
          ) : null}
          {error ? (
            <div className="col-span-12">
              <Alert
                type="error"
                message={
                  error?.message || "Error updating notifications settings."
                }
                errors={error?.errors}
              />
            </div>
          ) : null}
          {loading ? (
            <SwitchSkeleton />
          ) : (
            <div className="border border-b border-gray-200 divide-y divide-gray-200 rounded col-span-12">
              <div className="relative flex items-start p-4">
                <div className="min-w-0 flex-1 text-sm">
                  <div className="flex gap-2 items-center">
                    <MailIcon width={24} />
                    <label
                      htmlFor="email"
                      className="font-medium text-gray-700 select-none"
                    >
                      {t("Email")}
                    </label>
                  </div>
                </div>
                <Switch
                  id="email"
                  checked={acceptEmail}
                  onChange={() => {
                    setacceptEmail((prevAcceptEmail) => !prevAcceptEmail);
                  }}
                  className={classNames(
                    acceptEmail ? "bg-blue-600" : "bg-gray-200",
                    "relative inline-flex flex-shrink-0 h-6 w-11 disabled:cursor-not-allowed border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      acceptEmail ? "translate-x-5" : "translate-x-0",
                      "bg-white pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200",
                    )}
                  />
                </Switch>
              </div>
              <div className="relative flex items-start p-4">
                <div className="min-w-0 flex-1 text-sm">
                  <div className="flex gap-2 items-center">
                    <img className="w-6" src={WhatsappIcon} alt="Whatsapp" />
                    <label
                      htmlFor="whatsapp"
                      className="font-medium text-gray-700 select-none"
                    >
                      {t("WhatsApp")}
                    </label>
                  </div>
                </div>
                <div className="ml-3 flex items-center h-5">
                  <Switch
                    id="whatsapp"
                    checked={acceptWhatsApp}
                    onChange={() => {
                      setacceptWhatsApp(
                        (prevAcceptWhatsApp) => !prevAcceptWhatsApp,
                      );
                    }}
                    className={classNames(
                      acceptWhatsApp ? "bg-blue-600" : "bg-gray-200",
                      "relative inline-flex flex-shrink-0 h-6 w-11 disabled:cursor-not-allowed border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        acceptWhatsApp ? "translate-x-5" : "translate-x-0",
                        "bg-white pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200",
                      )}
                    />
                  </Switch>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 py-4 px-4 flex justify-end sm:px-6">
        <Button
          type="submit"
          title={t("Save")}
          disabled={loading}
          loading={loading}
        />
      </div>
    </form>
  );
}

const subTables = [
  {
    value: "REGISTRATION",
    label: "registrationLabel",
  },
  {
    value: "POINT",
    label: "pointLabel",
  },
];

function ReferralSettings() {
  const { t, i18n } = useTranslation();
  const authDispatch = useAuthDispatch();
  const {
    request,
    loading,
    error,
    user,
    referralHistories,
    referralHistoriesLoading,
    referralHistoriesError,
    referralHistoryNextTokens,
    referralTransactions,
    referralTransactionNextTokens,
    referralTransactionsLoading,
    referralTransactionsError,
  } = useAuthState();

  const [isCopied, setIsCopied] = useState(false);
  const [page, setpage] = useState(1);
  const [selectedTable, setselectedTable] = useState(subTables[0]);

  const isRegistration = selectedTable?.value === "REGISTRATION";

  const handleCopyReferralCode = () => {
    navigator.clipboard.writeText(user.referralID);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  useEffect(() => {
    if (user?.referralID) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.referralID, isRegistration]);

  const onLoad = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (user?.referralID) {
      const requestFunction = isRegistration
        ? requestListReferralHistory
        : requestListPointTransactionReferral;

      requestFunction(authDispatch, {
        referralID: user.referralID,
        page: 1,
        limit: 10,
      });
    }
  };

  const handlePageChange = (newPage) => {
    setpage(newPage);

    const nextTokens = isRegistration
      ? referralHistoryNextTokens[newPage]
      : referralTransactionNextTokens[newPage];

    if (nextTokens || newPage === 1) {
      const requestFunction = isRegistration
        ? requestListReferralHistory
        : requestListPointTransactionReferral;

      requestFunction(authDispatch, {
        referralID: user.referralID,
        page: newPage,
        limit: 10,
        nextTokens,
      });
    }
  };

  const onNext = () => handlePageChange(page + 1);
  const onPrev = () => page > 1 && handlePageChange(page - 1);
  const onPageChange = (selectedPage) => handlePageChange(selectedPage);

  return (
    <div className="divide-y divide-gray-200 lg:col-span-9">
      <div className="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {t("inviteReferral")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("inviteReferralDescription")}
          </p>
        </div>

        {error ? (
          <div className="col-span-12 mt-6">
            <Alert
              type="error"
              message={error?.message || "Error create referral code."}
              errors={error?.errors}
            />
          </div>
        ) : null}

        {!user.referralID ? (
          <div className="mt-6 bg-white border border-gray-200 shadow-sm rounded-lg p-6">
            <div className="flex space-y-4 justify-between items-center">
              <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-900">
                  {t("referralCode")}
                </p>
                <p className="text-sm text-gray-600">
                  {t(
                    "referralCodeDescription",
                    "Generate a code to invite users.",
                  )}
                </p>
              </div>
              <Button
                title={t("createReferralCode")}
                disabled={loading}
                loading={loading}
                onClick={() => {
                  requestGetReferral(authDispatch);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="w-full mt-6 bg-white flex flex-col gap-6">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900">
                {t("referralCode")}
              </h3>
              <div
                className="flex items-center justify-between p-3 rounded bg-blue-50 border-2 border-dashed border-blue-400 cursor-pointer"
                onClick={handleCopyReferralCode}
              >
                <span className="text-sm font-medium">{user.referralID}</span>
                <button
                  onClick={handleCopyReferralCode}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                >
                  {isCopied ? t("codeCopied") : t("tapToCopy")}
                </button>
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              <div>
                <Listbox
                  value={selectedTable}
                  onChange={(newTable) => {
                    setselectedTable(newTable);
                    setpage(1);
                  }}
                >
                  {({ open }) => (
                    <>
                      <ListboxButton className="bg-white flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm">
                        <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                          {t(selectedTable?.label)}
                        </span>
                        <SelectorIcon className="w-5 h-5 text-gray-500" />
                      </ListboxButton>
                      <Transition
                        show={open}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-48">
                          {subTables.map((item) => (
                            <ListboxOption
                              key={item.value}
                              className={({ focus }) =>
                                classNames(
                                  focus
                                    ? "text-white bg-blue-600"
                                    : "text-gray-900",
                                  "cursor-default select-none relative py-2 pl-3 pr-9",
                                )
                              }
                              value={item}
                            >
                              {({ selected, focus }) => (
                                <>
                                  <div className="flex items-center justify-between">
                                    <span
                                      className={classNames(
                                        selected
                                          ? "font-semibold"
                                          : "font-normal",
                                        "ml-3 mr-2 block truncate",
                                      )}
                                    >
                                      {t(item.label)}
                                    </span>
                                    {selected ? (
                                      <span
                                        className={classNames(
                                          focus
                                            ? "text-white"
                                            : "text-blue-600",
                                          "absolute inset-y-0 right-0 flex items-center pr-4",
                                        )}
                                      >
                                        <CheckIcon className="h-5 w-5" />
                                      </span>
                                    ) : null}
                                  </div>
                                </>
                              )}
                            </ListboxOption>
                          ))}
                        </ListboxOptions>
                      </Transition>
                    </>
                  )}
                </Listbox>
              </div>
              <p className="text-sm text-gray-600">
                {isRegistration
                  ? t("referralHistoryDescription")
                  : t("referralTransactionDescription")}
              </p>
              <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8 mt-4">
                <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                    {isRegistration ? (
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                              {t("ID")}
                            </th>
                            <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                              {t("Referral ID")}
                            </th>
                            <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                              {t("Date")}
                            </th>
                          </tr>
                        </thead>
                        {referralHistoriesLoading ? (
                          <TableSkeleton rows={5} columns={3} />
                        ) : (
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {referralHistories.length > 0 ? (
                              referralHistories.map((referral) => {
                                const shortId = referral.id.substr(
                                  referral.id.length - 5,
                                );
                                return (
                                  <tr key={referral.id}>
                                    <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap uppercase">
                                      {shortId}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                      {referral.referralID}
                                    </td>

                                    <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                      {dayjs(referral.createdAt)
                                        .locale(i18n.language)
                                        .format("DD MMM YYYY")}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td className="p-4" colSpan={8}>
                                  {referralHistoriesError ? (
                                    <ErrorList
                                      message={
                                        referralHistoriesError?.message ||
                                        "Error fetching referral histories."
                                      }
                                      errors={referralHistoriesError?.errors}
                                    />
                                  ) : (
                                    <EmptyList />
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        )}
                      </table>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                              {t("Date")}
                            </th>
                            <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                              {t("Description")}
                            </th>
                            <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                              {t("point")}
                            </th>
                          </tr>
                        </thead>
                        {referralTransactionsLoading ? (
                          <TableSkeleton rows={5} columns={3} />
                        ) : (
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {referralTransactions.length > 0 ? (
                              referralTransactions.map((item) => {
                                return (
                                  <tr key={item.id}>
                                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap ">
                                      {dayjs(item.createdAt)
                                        .locale(i18n.language)
                                        .format("DD MMM YYYY")}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                      {item.description}
                                    </td>

                                    <td className="px-4 py-2 text-sm text-left text-green-600 whitespace-nowrap">
                                      +{item.total?.toLocaleString("id-ID")}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td className="p-4" colSpan={8}>
                                  {referralTransactionsError ? (
                                    <ErrorList
                                      message={
                                        referralTransactionsError?.message ||
                                        "Error fetching referral histories."
                                      }
                                      errors={referralTransactionsError?.errors}
                                    />
                                  ) : (
                                    <EmptyList />
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        )}
                      </table>
                    )}
                    <Pagination
                      page={page}
                      onPrev={onPrev}
                      onNext={onNext}
                      onPageChange={onPageChange}
                      nextTokens={
                        isRegistration
                          ? referralHistoryNextTokens
                          : referralTransactionNextTokens
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const subNavigation = [
  {
    value: "profile",
    label: "Profile",
    icon: UserCircleIcon,
    component: EditProfile,
  },
  {
    value: "npwp",
    label: "NPWP",
    icon: CreditCardIcon,
    component: NpwpService,
  },
  {
    value: "password",
    label: "Password",
    icon: KeyIcon,
    component: EditPassword,
  },
  {
    value: "notification",
    label: "Notification",
    icon: BellIcon,
    component: NotificationSettings,
  },
  {
    value: "consultant",
    label: "Consultant Settings",
    icon: CogIcon,
    component: ConsultantSettings,
  },
  {
    value: "template",
    label: "Consultant Template",
    icon: ChatAltIcon,
    component: ConsultantTemplate,
  },
  {
    value: "mfa",
    label: "MFA",
    icon: LockClosedIcon,
    component: PreferredMFA,
  },
  {
    value: "referral",
    label: "Referral",
    icon: GiftIcon,
    component: ReferralSettings,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  const { id } = useParams();

  const [activeSubNavigation, setactiveSubNavigation] = useState("profile");

  useEffect(() => {
    if (!user) {
      navigate("/settings");
      return;
    }

    const shouldSetActive =
      subNavigation.some((item) => item.value === id) &&
      ((id === "npwp" && user.registeredNpwp?.id) ||
        (id === "template" && user.membership === "CONSULTANT") ||
        (id === "consultant" && user.membership === "CONSULTANT"));

    if (shouldSetActive) {
      setactiveSubNavigation(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user.membership, user.registeredNpwp, navigate, subNavigation]);

  const filteredSubNavigation =
    user.membership === "CONSULTANT"
      ? subNavigation
      : subNavigation.filter(
          (item) => item.value !== "consultant" && item.value !== "template",
        );

  return (
    <div className="max-w-screen-xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
          <aside className="py-6 lg:col-span-3">
            <nav className="space-y-1">
              {user.registeredNpwp?.id
                ? filteredSubNavigation.map((item) => (
                    <button
                      type="button"
                      key={item.value}
                      onClick={() => {
                        setactiveSubNavigation(item.value);
                      }}
                      className={classNames(
                        item.value === activeSubNavigation
                          ? "bg-teal-50 border-blue-500 text-blue-700 hover:bg-teal-50 hover:text-blue-700"
                          : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                        "group border-l-4 px-3 py-2 inline-flex w-full items-center text-sm font-medium",
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.value === activeSubNavigation
                            ? "text-blue-500 group-hover:text-blue-500"
                            : "text-gray-400 group-hover:text-gray-500",
                          "flex-shrink-0 -ml-1 mr-3 h-6 w-6",
                        )}
                        aria-hidden="true"
                      />
                      <span className="truncate">{t(item.label)}</span>
                    </button>
                  ))
                : filteredSubNavigation
                    .filter((item) =>
                      user.registeredNpwp?.id
                        ? item.value
                        : item.value === "profile" ||
                          item.value === "password" ||
                          item.value === "consultant" ||
                          item.value === "template" ||
                          item.value === "mfa" ||
                          item.value === "notification" ||
                          item.value === "referral",
                    )
                    .map((item) => (
                      <button
                        type="button"
                        key={item.value}
                        onClick={() => setactiveSubNavigation(item.value)}
                        className={classNames(
                          item.value === activeSubNavigation
                            ? "bg-teal-50 border-blue-500 text-blue-700 hover:bg-teal-50 hover:text-blue-700"
                            : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                          "group border-l-4 px-3 py-2 inline-flex w-full items-center text-sm font-medium",
                        )}
                      >
                        <item.icon
                          className={classNames(
                            item.value === activeSubNavigation
                              ? "text-blue-500 group-hover:text-blue-500"
                              : "text-gray-400 group-hover:text-gray-500",
                            "flex-shrink-0 -ml-1 mr-3 h-6 w-6",
                          )}
                          aria-hidden="true"
                        />
                        <span className="truncate">{t(item.label)}</span>
                      </button>
                    ))}
            </nav>
          </aside>
          {subNavigation.map((item) => {
            const { value, component: Component } = item;
            if (value === activeSubNavigation) return <Component key={value} />;
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

export default Settings;
